import React, { useState, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";

const faqs = [
  {
    id: 1,
    question: "How long does it take to become fluent in a new language?",
    answer:
      "The time to achieve fluency varies based on several factors including your native language, the target language, your dedication, and prior language learning experience. On average, our students reach conversational fluency (B1 level) in 6-8 months with regular practice. Complete fluency (C1 level) typically takes 1-2 years of consistent study and immersion.",
  },
  {
    id: 2,
    question: "What makes Language Village different from other platforms?",
    answer:
      "Language Village combines personalized instruction from certified native-speaking teachers, adaptive technology that adjusts to your learning pace, and a community-based approach that provides authentic conversation practice. Unlike app-only solutions, we focus on practical communication skills through real human interaction and cultural context.",
  },
  {
    id: 3,
    question: "How are the lessons structured?",
    answer:
      "Our lessons are 50 minutes long and follow a structured yet flexible format. Each session includes review of previous material, introduction of new concepts, guided practice, conversation practice, and personalized feedback. Lessons are tailored to your learning style and goals, with materials provided before each class for preparation.",
  },
  {
    id: 4,
    question: "Can I change my instructor if needed?",
    answer:
      "Yes, we understand the importance of a good student-teacher connection. If you feel your current instructor isn't the right fit, you can request a change at any time through your student dashboard. We'll match you with another qualified instructor who may better suit your learning style and goals.",
  },
  {
    id: 5,
    question: "What if I need to cancel or reschedule a lesson?",
    answer:
      "We offer flexible rescheduling. You can cancel or reschedule a lesson up to 24 hours before the scheduled time without any penalty. Changes made with less than 24 hours' notice may result in the lesson being counted as completed, depending on your subscription plan.",
  },
  {
    id: 6,
    question: "Do you offer group classes or just private lessons?",
    answer:
      "We offer both private one-on-one lessons and small group classes (maximum 4 students). Group classes are arranged by proficiency level to ensure everyone can participate effectively. Many students choose a combination of both formats to balance personalized attention with collaborative learning.",
  },
];

const FaqSection = () => {
  const [openItem, setOpenItem] = useState(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const toggleItem = (id) => {
    setOpenItem(openItem === id ? null : id);
  };

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
      id="faq"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full -translate-x-1/3 translate-y-1/3"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Frequently Asked <span className="text-primary">Questions</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about our language learning
            programs, methodology, and student experience.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="mb-4"
            >
              <button
                onClick={() => toggleItem(faq.id)}
                className={`w-full text-left p-5 rounded-xl flex justify-between items-center transition-all duration-300 ${
                  openItem === faq.id
                    ? "bg-primary text-white shadow-md"
                    : "bg-white hover:bg-gray-50 text-gray-800 shadow-sm"
                }`}
              >
                <span className="font-medium text-lg">{faq.question}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-5 w-5 transition-transform duration-300 ${
                    openItem === faq.id ? "transform rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <AnimatePresence>
                {openItem === faq.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-5 bg-white border border-gray-100 rounded-b-xl shadow-sm">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-gray-600 mb-4">Still have questions?</p>
          <button className="bg-primary hover:bg-primary-dark text-white font-medium py-3 px-8 rounded-full transition duration-300 shadow-md hover:shadow-lg">
            Contact Our Support Team
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default FaqSection;
